/* eslint-disable @typescript-eslint/no-explicit-any -- any must be used here */
import { v4 as uuidv4 } from 'uuid';

import { apiConfig } from '@/api/config';
import {
  AbortSignalError,
  BadRequestError,
  HttpClientError,
  NetworkError,
  NotFoundError,
  PreconditionFailedError,
  ServerError,
} from '@/api/errors';
import { type CommonHeaders } from '@/api/headers';
import { type ApiLogger } from '@/api/logger/apiLogger';
import { type KeyGenHeader } from '@/api/types/keyGenTypes';
import { centralizedLogin } from '@/sdk/client/centralizedLogin';

const { isLogResponseHeadersEnabled, appVersion, platform, productBaseUrl } = apiConfig;

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface HttpOptions {
  signal?: AbortSignal;
  dma?: string;
  home_dma?: string;
  home_zip_code?: string;
  keyGenHeaders?: KeyGenHeader[];
  location?: string;
  zip_code?: string;
}

interface OSDetector {
  detect: () => Promise<{ os: string; osVersion: string } | { ua: string }>;
  extract: (ua: string) => { os: string; osVersion: string };
}

/**
 * HttpClient is a wrapper around the fetch API that adds some useful features:
 * - Adds the access token and api key to the headers
 * - Parses the response JSON as `<T = any>` object if response is ok
 * - Retries only once if the access token is expired by forcing refresh token (401 unauthorized)
 * - Throws {@link HttpClientError} if the response is not ok (except 401 when not already retried)
 * - Supports aborting the request with an {@link AbortSignal}
 * - Logs the request and response with {@link ApiLogger} if provided
 */
export class HttpClient {
  private readonly baseUrl: string;
  private readonly headers: CommonHeaders;
  private readonly osDetector: OSDetector;
  protected readonly logger: ApiLogger;

  public constructor(options: { baseUrl: string; headers: CommonHeaders; logger: ApiLogger; osDetector: OSDetector }) {
    const { baseUrl, headers, logger, osDetector } = options;

    this.baseUrl = baseUrl;
    this.headers = headers;
    this.osDetector = osDetector;
    this.logger = logger;
  }

  // #region public methods
  /**
   * Get requests assumes that a response body will always be present so data will always de defined, if not an error will be thrown
   * @throws {@link HttpClientError} if the response is not ok
   */
  public async get<T = any>(endpoint: string, options?: HttpOptions): Promise<T> {
    const method = 'GET';

    const data = await this.fetchData<T>(endpoint, method, undefined, options);

    // returned data is always defined for a GET request
    return data as T;
  }

  /**
   * Delete requests assumes that a response body will always be present so data will always de defined
   * @throws {@link HttpClientError} if the response is not ok
   */
  public async delete<T = any, U = any>(
    endpoint: string,
    requestBody?: U,
    options?: HttpOptions,
  ): Promise<T | undefined> {
    const method = 'DELETE';

    return await this.fetchData<T>(endpoint, method, requestBody, options);
  }

  /**
   * Post requests assumes that a response body will always be present so data will always de defined
   * @throws {@link HttpClientError} if the response is not ok
   */
  public async post<T = any, U = any>(
    endpoint: string,
    requestBody?: U,
    options?: HttpOptions,
  ): Promise<T | undefined> {
    const method = 'POST';

    const data = await this.fetchData<T, U>(endpoint, method, requestBody, options);

    return data;
  }

  /**
   * Put requests can return empty response body so data could be undefined
   * @throws {@link HttpClientError} if the response is not ok
   */
  public async put<T = any, U = any>(endpoint: string, requestBody: U, options?: HttpOptions): Promise<T | undefined> {
    const method = 'PUT';

    return await this.fetchData<T, U>(endpoint, method, requestBody, options);
  }
  // #endregion

  // #region protected methods
  protected async getAccessToken(): Promise<string | undefined> {
    let accessToken: string | undefined;

    try {
      const refreshToken = await centralizedLogin.checkRefreshToken({});

      accessToken = refreshToken.accessToken;
    } catch {}

    return accessToken;
  }
  // #endregion

  // #region private methods
  /**
   * Make fetch requests directly requests can return empty response body so data could be undefined
   * @throws {@link HttpClientError} if the response is not ok
   */
  private async fetchData<T = any, U = any>(
    endpoint: string,
    method: Method,
    requestBody?: U,
    options?: HttpOptions,
  ): Promise<T | undefined> {
    const response = await this.fetchRequest<U>(endpoint, method, requestBody, options);

    // when status is 204 (no content) we do not parse the response because data is undefined, just return undefined
    if (response.status === 204) {
      this.logResponse('debug', endpoint, method, response);

      return undefined;
    }

    let data;

    try {
      data = await response.json();
    } catch {}

    if (data?.data) {
      data = data.data;
    }

    if (response.ok) {
      this.logResponse('debug', endpoint, method, response, data);

      return data;
    }

    // error handling
    this.logResponse('error', endpoint, method, response, data);

    const message: string = data?.errors?.[0] ?? response.statusText ?? 'An error occurred';

    if (response.status >= 500) {
      throw new ServerError(message, response.status);
    }

    if (response.status === 404) {
      throw new NotFoundError(message);
    }

    if (response.status === 400) {
      throw new BadRequestError(message);
    }

    if (response.status === 412) {
      throw new PreconditionFailedError(message);
    }

    throw new HttpClientError(message, response.status);
  }

  private async fetchRequest<U = any>(
    endpoint: string,
    method: Method,
    requestBody?: U,
    options: HttpOptions = {},
    isRetry = false,
  ): Promise<Response> {
    if (!options.keyGenHeaders && this.baseUrl === productBaseUrl) {
      if (!this.logger.namespace.startsWith('KeyGen') && !this.logger.namespace.startsWith('Config')) {
        this.logger.error('[fetchRequest], keyGenHeaders is not defined', { endpoint });
      }
    }

    const headers = await this.createHeaders(options);

    const init: RequestInit = {
      method,
      headers,
      cache: 'no-store',
      next: { revalidate: 0 },
    };

    if (options.signal) {
      init.signal = options.signal;
    }

    if (requestBody) {
      init.body = JSON.stringify(requestBody);
    }

    this.logRequest(endpoint, method, headers, requestBody);

    let response: Response;

    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, init);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new AbortSignalError(error.message);
      }

      this.logNetworkError(endpoint);

      throw new NetworkError(error instanceof Error ? error.message : 'Error');
    }

    // 401 Unauthorized, let's retry one time
    if (response.status === 401 && !isRetry) {
      return await this.fetchRequest<U>(endpoint, method, requestBody, options, true);
    }

    return response;
  }

  private async createHeaders(options: HttpOptions): Promise<Headers> {
    const { home_dma, dma, home_zip_code, zip_code, location, keyGenHeaders } = options;

    const data = await this.osDetector.detect();
    const { os, osVersion } = 'ua' in data ? this.osDetector.extract(data.ua) : data;
    const client = `app_version:${appVersion};os:${os};os_version:${osVersion};platform:${platform}`;

    const requestHeaders = new Headers();

    keyGenHeaders?.forEach(({ key, value }) => {
      if (key && value) {
        requestHeaders.set(key, value);
      }
    });

    for (const [key, value] of this.headers.entries()) {
      if (value.includes('{client}')) {
        requestHeaders.set(key, value.replace('{client}', client));

        continue;
      }

      if (value.includes('{uuid}')) {
        requestHeaders.set(key, value.replace('{uuid}', uuidv4()));
        continue;
      }

      if (value.includes('{accessToken}')) {
        const accessToken = await this.getAccessToken();

        if (accessToken) {
          requestHeaders.set(key, value.replace('{accessToken}', accessToken));
        }

        continue;
      }

      if (value.includes('{home_dma}')) {
        if (home_dma) {
          requestHeaders.set(key, value.replace('{home_dma}', home_dma));
        }

        continue;
      }

      if (value.includes('{dma}')) {
        if (dma) {
          requestHeaders.set(key, value.replace('{dma}', dma));
        }

        continue;
      }

      if (value.includes('{home_zip_code}')) {
        if (home_zip_code) {
          requestHeaders.set(key, value.replace('{home_zip_code}', home_zip_code));
        }

        continue;
      }

      if (value.includes('{zip_code}')) {
        if (zip_code) {
          requestHeaders.set(key, value.replace('{zip_code}', zip_code));
        }

        continue;
      }

      if (value.includes('{location}')) {
        if (location) {
          requestHeaders.set(key, value.replace('{location}', location));
        }

        continue;
      }

      requestHeaders.set(key, value);
    }

    return requestHeaders;
  }

  private logRequest(endpoint: string, method: Method, headers: Headers, body?: any): void {
    if (!apiConfig.isLogEnabled) {
      return;
    }

    const url = encodeURI(`${this.baseUrl}${endpoint}`);
    const logMsg = `fetch: ${method} ${url}`;

    const requestHeaders: Record<string, string> = {};

    for (const [key, value] of headers.entries()) {
      requestHeaders[key] = value;
    }

    if (method === 'GET') {
      this.logger.debug(logMsg, { requestHeaders });
    } else {
      this.logger.debug(logMsg, { requestHeaders, requestBody: body });
    }
  }

  private logNetworkError(endpoint: string): void {
    const url = encodeURI(`${this.baseUrl}${endpoint}`);
    const logMsg = `'NetworkError: ${url}`;

    this.logger.error(logMsg);
  }

  private logResponse(type: 'debug' | 'error', endpoint: string, method: Method, response: Response, body?: any): void {
    if (!apiConfig.isLogEnabled) {
      return;
    }

    const { headers, status } = response;

    const url = encodeURI(`${this.baseUrl}${endpoint}`);
    const logMsg = `(${status}) ${method} ${url}`;

    const responseHeaders: Record<string, string> = {};

    for (const [key, value] of headers.entries()) {
      responseHeaders[key] = value;
    }

    if (isLogResponseHeadersEnabled) {
      this.logger[type](logMsg, { responseHeaders, body });
    } else {
      this.logger[type](logMsg, { responseBody: body });
    }
  }
  // #endregion
}
